import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchGarage = createAsyncThunk('garage/fetch', async (userId, { rejectWithValue }) => {
  try {
    const [garageRes, vehiclesRes, valueRes] = await Promise.all([
      api.get(`/garages/${userId}`),
      api.get(`/garages/${userId}/vehicles`),
      api.get(`/garages/${userId}/value`)
    ]);
    return {
      garage: garageRes.data.garage,
      liked: !!garageRes.data.liked,
      vehicles: vehiclesRes.data.vehicles,
      value: valueRes.data,
    };
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to load garage');
  }
});

export const toggleGarageLike = createAsyncThunk('garage/toggleLike', async ({ userId, liked }, { rejectWithValue }) => {
  try {
    if (liked) await api.delete(`/garages/${userId}/like`);
    else await api.post(`/garages/${userId}/like`);
    return { liked: !liked };
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed');
  }
});

export const addVehicle = createAsyncThunk('garage/addVehicle', async ({ userId, data }, { rejectWithValue }) => {
  try {
    const res = await api.post(`/garages/${userId}/vehicles`, data);
    return res.data.userVehicle;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to add vehicle');
  }
});

export const removeVehicle = createAsyncThunk('garage/removeVehicle', async ({ userId, userVehicleId }, { rejectWithValue }) => {
  try {
    await api.delete(`/garages/${userId}/vehicles/${userVehicleId}`);
    return userVehicleId;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to remove vehicle');
  }
});

export const updateGarage = createAsyncThunk('garage/update', async ({ userId, data }, { rejectWithValue }) => {
  try {
    const res = await api.put(`/garages/${userId}`, data);
    return res.data.garage;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message);
  }
});

const garageSlice = createSlice({
  name: 'garage',
  initialState: { garage: null, liked: false, vehicles: [], value: null, loading: false, error: null },
  reducers: {
    clearGarage(state) { state.garage = null; state.liked = false; state.vehicles = []; state.value = null; }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGarage.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchGarage.fulfilled, (state, action) => {
        state.loading = false;
        state.garage = action.payload.garage;
        state.liked = action.payload.liked;
        state.vehicles = action.payload.vehicles;
        state.value = action.payload.value;
      })
      .addCase(fetchGarage.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(addVehicle.fulfilled, (state, action) => { state.vehicles.unshift(action.payload); })
      .addCase(removeVehicle.fulfilled, (state, action) => {
        state.vehicles = state.vehicles.filter(v => v.id !== action.payload);
      })
      .addCase(updateGarage.fulfilled, (state, action) => { state.garage = action.payload; })
      .addCase(toggleGarageLike.fulfilled, (state, action) => {
        const nowLiked = action.payload.liked;
        state.liked = nowLiked;
        if (state.garage) {
          state.garage.likeCount = Math.max(0, (state.garage.likeCount || 0) + (nowLiked ? 1 : -1));
        }
      });
  }
});

export const { clearGarage } = garageSlice.actions;
export default garageSlice.reducer;
